import React, { useContext, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import { CompanyFolderOutDTO, EntityTypeEnum, FolderVisibilityEnum } from '@api/ringil-api.v2';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { ToastContext } from '@components/auth/ToastContext.tsx';
import { NoWrapText } from '@components/styles.tsx';
import { EditCompanyFolderSideBar } from '@components/pages/admin/folder/EditCompanyFolderSidebar.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import SectionPanel from '@components/framework/panels/SectionPanel.tsx';
import RingilDataTable, { ColumnConfig } from '@components/RingilDataTable/RingilDataTable.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import SvgFolder from '@icons/folder.svg?react';

interface ComponentProps {
  /** admin = Ringil admin on the company detail (companyId required); customer = Company profile of the current company */
  mode: 'admin' | 'customer';
  companyId?: string;
}

/**
 * RD-2977: folders of a company with their visibility (everyone / selected departments + users), external id
 * and the number of filed expeditions. Shared by the admin company detail and the customer's Company profile.
 */
const CompanyFoldersTable: React.FC<ComponentProps> = ({ mode, companyId }) => {
  const { listCompanyFolder, deleteCompanyFolder, listCompanyFoldersCustomer, deleteCompanyFolderCustomer } = useContext(ApiContext);
  const { showToastMessage } = useContext(ToastContext);
  const { tr } = useTranslationLgs();
  const [editor, setEditor] = useState<{ visible: boolean; folder?: CompanyFolderOutDTO }>({ visible: false });
  const [toDelete, setToDelete] = useState<CompanyFolderOutDTO | null>(null);
  // RD-2977: folder types offered in the editor = the company's modules (BE decides)
  const [availableEntityTypes, setAvailableEntityTypes] = useState<EntityTypeEnum[]>();
  const tableRef = useRef<{ reloadData: () => void; selectedObjects: unknown[]; clear: () => void }>(null);

  const reload = () => tableRef.current?.reloadData();

  const remove = (folder: CompanyFolderOutDTO) => {
    const done = () => {
      showToastMessage(tr('generic.done', 'Done'), tr('CompanyFolders.folderDeleted', 'Složka byla smazána'), 'success');
      setToDelete(null);
      reload();
    };
    if (mode === 'admin') deleteCompanyFolder(companyId, folder.folderId, done);
    else deleteCompanyFolderCustomer(folder.folderId, done);
  };

  const renderVisibility = (row: CompanyFolderOutDTO) => {
    if (row.folderVisibility !== FolderVisibilityEnum.RESTRICTED) return <span>{tr('CompanyFolders.visibilityAll', 'Všichni')}</span>;
    const parts: string[] = [];
    if (row.departments?.length) parts.push(tr('CompanyFolders.departmentsCount', '{{count}} oddělení', { count: row.departments.length }, row.departments.length));
    if (row.users?.length) parts.push(tr('CompanyFolders.usersCount', '{{count}} uživatelů', { count: row.users.length }, row.users.length));
    return (
      <span title={[...(row.departments ?? []), ...(row.users ?? [])].map(t => t.name).join(', ')}>
        {parts.length ? parts.join(', ') : tr('CompanyFolders.visibilityNobody', 'Nikdo')}
      </span>
    );
  };

  const renderActions = (row: CompanyFolderOutDTO) => (
    <NoWrapText>
      <Button label={tr('CompanyFolders.editFolder', 'Upravit')} className={'p-button-text'} onClick={() => setEditor({ visible: true, folder: row })} />
      <Button label={tr('CompanyFolders.removeFolder', 'Smazat')} className={'p-button-text p-button-danger'} onClick={() => setToDelete(row)} />
    </NoWrapText>
  );

  const columnConfig: ColumnConfig<CompanyFolderOutDTO>[] = [
    { id: 'folderName', header: 'CompanyFolders.folderName', headerDefault: 'Název', verticalAlign: 'middle', render: row => <div>{row.folderName}</div> },
    { id: 'entityType', header: 'CompanyFolders.entityType', headerDefault: 'Typ', verticalAlign: 'middle', render: row => <div>{tr(`EntityType.${row.entityType}`, row.entityType)}</div> },
    { id: 'externalId', header: 'CompanyFolders.externalId', headerDefault: 'Externí ID', verticalAlign: 'middle', render: row => <div>{row.externalId}</div> },
    { id: 'visibility', header: 'CompanyFolders.visibility', headerDefault: 'Viditelnost', verticalAlign: 'middle', render: row => renderVisibility(row) },
    { id: 'expeditionCount', header: 'CompanyFolders.entityCount', headerDefault: 'Záznamů', verticalAlign: 'middle', width: '6rem', render: row => <div>{row.expeditionCount}</div> },
    { id: 'actions', width: '12rem', verticalAlign: 'middle', render: row => renderActions(row) },
  ];

  const load = (onSuccess: (d: { data: CompanyFolderOutDTO[]; total: number; page: number; size: number }) => void) => {
    // the generator derives a separate enum for the inline `availableEntityTypes` array; the values are EntityType names
    const apply = (d: { folders?: CompanyFolderOutDTO[]; availableEntityTypes?: string[] }) => {
      setAvailableEntityTypes((d?.availableEntityTypes ?? []) as EntityTypeEnum[]);
      onSuccess({ data: d?.folders ?? [], total: d?.folders?.length ?? 0, page: 1, size: d?.folders?.length ?? 0 });
    };
    if (mode === 'admin') listCompanyFolder(companyId, null, apply);
    else listCompanyFoldersCustomer(null, apply);
  };

  return (
    <SectionPanel
      icon={<SvgFolder className={'w-6 h-6'} />}
      iconColor={'var(--content-tertiary)'}
      heading={tr('CompanyFolders.companyFolders', 'Složky')}
      headerRight={<Button label={tr('CompanyFolders.addFolder', 'Přidat složku')} onClick={() => setEditor({ visible: true, folder: undefined })} />}
      dataTestId={mode === 'admin' ? 'admin-company-section-folders' : 'company-profile-section-folders'}
    >
      <RingilDataTable
        ref={tableRef}
        columnConfig={columnConfig as ColumnConfig<unknown>[]}
        filterName={mode === 'admin' ? 'admin_company_folders' : 'company_profile_folders'}
        hidePaginator={true}
        loadDataFromApi={(_filter, _pageable, onSuccess) => load(onSuccess)}
      />

      <EditCompanyFolderSideBar
        mode={mode}
        companyId={companyId}
        entityTypes={availableEntityTypes}
        folder={editor.folder}
        visible={editor.visible}
        onHide={saved => {
          setEditor({ visible: false, folder: undefined });
          if (saved) reload();
        }}
      />

      <Dialog
        visible={toDelete != null}
        onHide={() => setToDelete(null)}
        header={tr('CompanyFolders.deleteFolderTitle', 'Smazat složku')}
        style={{ width: '32rem' }}
      >
        <div className={'flex flex-col gap-4'}>
          <div>
            {tr(
              'CompanyFolders.deleteFolderText',
              'Složka "{{name}}" bude smazána. {{count}} záznamů v ní zůstane bez složky a bude viditelných pro všechny uživatele společnosti.',
              { name: toDelete?.folderName, count: toDelete?.expeditionCount ?? 0 },
            )}
          </div>
          <div className={'flex flex-row gap-2 justify-end'}>
            <RingilButton mode={'outlined'} label={tr('generic.cancel', 'Zrušit')} onClick={() => setToDelete(null)} />
            <RingilButton mode={'filled-rectangle'} label={tr('CompanyFolders.removeFolder', 'Smazat')} onClick={() => toDelete && remove(toDelete)} />
          </div>
        </div>
      </Dialog>
    </SectionPanel>
  );
};

export default CompanyFoldersTable;
